import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Terminal,
  Database,
  Settings,
  // Shield,
  Users,
  Server,
  Key,
  Cog,
  Share2,
  BookMarked,
  Layout,
  BookOpen,
  Compass,
  UserCircle,
  // LogOut,
  // Save,
  Clock,
  History,
  MessageSquare,
  Brain,
  // Download,
  AlertTriangle,
  // RefreshCw,
  X,
  // PlayCircle,
  // Plus,
} from "lucide-react";
import ErrorBoundary from "./services/ErrorBoundary";
import LoadingScreen from "./components/LoadingScreen";
import { useAuth } from "./hooks/useAuth";
import { useMonitoring } from "./hooks/useMonitoring";
import { QueryResult } from "./services/DatabaseAPIService";
import { api, QueryExecutePayload, SaveQueryPayload } from "./common/api";
// import { validateSqlQuery, validateInputSecurity } from "./utils/validation";
import Button from "./components/ui/Button";
// import { NotebookList } from "./components/NotebookList";
// import { NotebookInterface } from "./components/NotebookInterface";
// import { MindMapBuilder } from "./components/MindMapBuilder";
import { Notification } from "./components/NotificationCenter";

// Lazy load components for better performance
const NotificationCenter = lazy(
  () => import("./components/NotificationCenter")
);

// Import types
import { QueryHistoryItem } from "./components/QueryHistory";
import { SavedQuery } from "./components/QueryLibrary";
import { TeamMember } from "./components/TeamPanel";
import {
  QueryPerformanceMetrics,
  QueryOptimizationSuggestion,
} from "./services/QueryPerformanceAnalytics";
import AppRoutes from "./routes/App.routes";
import UserMenu from "./components/UserMenu";

// Define DatabaseConnection interface locally since we're removing demo connections
interface DatabaseConnection {
  id: string;
  name: string;
  type: string;
  host: string;
  port: string;
  database: string;
  username: string;
  password?: string;
  provider?: string;
  region?: string;
  connectionString?: string;
  ssl?: boolean;
  tags?: string[];
  status: "connected" | "disconnected";
  description: string;
  userId?: string;
}

const App: React.FC = () => {
  const { user, isAdmin, login, logout } = useAuth();
  const { logEvent, logError } = useMonitoring();
  const navigate = useNavigate();
  const location = useLocation();

  // State management with proper typing
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [naturalQuery, setNaturalQuery] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [connections, setConnections] = useState<DatabaseConnection[]>([]);
  const [selectedConnection, setSelectedConnection] =
    useState<DatabaseConnection | null>(null);
  // const [view] = useState<"query" | "notebook" | "builder" | "dashboard">(
  //   "query"
  // );
  const [queryHistory, setQueryHistory] = useState<QueryHistoryItem[]>([]);
  const [performanceMetrics] = useState<{
    metrics: QueryPerformanceMetrics;
    suggestions: QueryOptimizationSuggestion[];
  } | null>(null);
  const [activeConnectionId, setActiveConnectionId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  // const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastActivity, setLastActivity] = useState(new Date());

  // Refs
  const resultsRef = React.useRef<HTMLDivElement>(null);

  // Team members state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: "1",
      name: "John Doe",
      email: "john@example.com",
      role: "admin" as const,
      status: "active" as const,
      lastActive: new Date(),
    },
    {
      id: "2",
      name: "Sarah Smith",
      email: "sarah@example.com",
      role: "editor" as const,
      status: "active" as const,
      lastActive: new Date(),
    },
  ]);

  // console.log(notifications, connections, teamMembers, setPerformanceMetrics);

  // Saved queries state
  const [savedQueries, setSavedQueries] = useState([
    {
      id: "1",
      name: "Monthly Water Quality Analysis",
      description:
        "Comprehensive analysis of water quality metrics across all stations",
      query:
        "SELECT station_id, AVG(ph_level) as avg_ph, AVG(temperature) as avg_temp FROM water_readings GROUP BY station_id",
      tags: ["water-quality", "monthly", "analysis"],
      favorite: true,
      lastRun: new Date(),
      folder: "Water Quality",
    },
    {
      id: "2",
      name: "Critical Stations Report",
      description: "Identifies monitoring stations with concerning readings",
      query:
        "SELECT * FROM water_readings WHERE ph_level < 6.5 OR ph_level > 8.5",
      tags: ["monitoring", "alerts", "critical"],
      favorite: false,
      lastRun: new Date(),
      folder: "Monitoring",
    },
  ]);

  // Network status monitoring
  // useEffect(() => {
  //   const handleOnline = () => {
  //     setIsOnline(true);
  //     logEvent("network_status_changed", { status: "online" });
  //   };

  // const handleOffline = () => {
  //   setIsOnline(false);
  //   logEvent("network_status_changed", { status: "offline" });
  //   toast.error(
  //     "You are currently offline. Some features may be unavailable."
  //   );
  // };

  //   window.addEventListener("online", handleOnline);
  //   // window.addEventListener("offline", handleOffline);

  //   return () => {
  //     window.removeEventListener("online", handleOnline);
  //     // window.removeEventListener("offline", handleOffline);
  //   };
  // }, [logEvent]);

  // Activity tracking
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(new Date());
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  useEffect(() => {
    const activeConnection = JSON.parse(
      localStorage.getItem("activeConnection") || "{}"
    );

    const activeConnectionId = activeConnection.id;
    if (!activeConnectionId) {
      // toast.error("No active connection found. Please select a connection.");
      return;
    }

    const token = JSON.parse(localStorage.getItem("token") || "null");
    if (!token?.accessToken) {
      // toast.error("User not authenticated. Please log in.");
      return;
    }

    api.setAuthToken(token.accessToken);

    api
      .getSavedQueries(activeConnectionId)
      .then((saved) => {
        console.log("Fetched saved queries:", saved);
        setQueryHistory(saved);
      })
      .catch((error) => {
        console.error("Error fetching saved queries:", error);
        // toast.error("Failed to fetch saved queries.");
      });
  }, []);

  // Auto-save and session management
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const now = new Date();
  //     const timeSinceActivity = now.getTime() - lastActivity.getTime();

  //     // Auto-save every 5 minutes of activity
  //     if (timeSinceActivity < 300000 && sqlQuery.trim()) {
  //       handleAutoSave();
  //     }

  //     // Session timeout after 24 hours (1 day) of inactivity
  //     if (timeSinceActivity > 86400000) {
  //       // 24 * 60 * 60 * 1000
  //       handleSessionTimeout();
  //     }
  //   }, 60000); // Check every minute

  //   return () => clearInterval(interval);
  // }, [lastActivity, sqlQuery]);

  // Initialize connections and load stored connection from localStorage
  useEffect(() => {
    // Load connections from localStorage only when user is authenticated
    if (!user) {
      // Clear state when user is not authenticated
      console.log("User logged out, clearing connection state");
      setConnections([]);
      setSelectedConnection(null);
      setActiveConnectionId(null);
      return;
    }

    console.log("User authenticated, loading connections from localStorage");
    const storedConnections = localStorage.getItem("connections");
    const storedActiveConnection = localStorage.getItem("activeConnection");

    if (storedConnections) {
      try {
        const parsedConnections = JSON.parse(storedConnections);
        console.log("Loaded connections:", parsedConnections);
        setConnections(parsedConnections);
      } catch (error) {
        console.error("Failed to parse stored connections:", error);
      }
    }

    if (storedActiveConnection) {
      try {
        const parsedActiveConnection = JSON.parse(storedActiveConnection);
        const connectionId =
          parsedActiveConnection.id || parsedActiveConnection.connection_id;
        console.log("Setting active connection:", parsedActiveConnection);
        console.log("Setting activeConnectionId to:", connectionId);
        setSelectedConnection(parsedActiveConnection);
        setActiveConnectionId(connectionId);
      } catch (error) {
        console.error("Failed to parse stored active connection:", error);
      }
    } else {
      console.log("No active connection found in localStorage");
    }
  }, [user]);

  // Connect to database when connection changes (only if authenticated)
  // useEffect(() => {
  //   if (selectedConnection && user) {
  //     connectToDatabase(selectedConnection);
  //   } else {
  //     setActiveConnectionId(null);
  //   }
  // }, [selectedConnection, user]);

  // Track activeConnectionId changes for debugging
  useEffect(() => {
    console.log("activeConnectionId changed to:", activeConnectionId);
  }, [activeConnectionId]);

  // Scroll to results when query result changes
  useEffect(() => {
    if (queryResult && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [queryResult]);

  // // Navigation handlers
  // const handleNavigateToApp = useCallback(() => {
  //   navigate("/");
  //   logEvent("navigation", { from: "landing", to: "app" });
  // }, [logEvent, navigate]);

  const handleNavigateToLanding = useCallback(() => {
    navigate("/");
    logEvent("navigation", { from: "app", to: "" });
  }, [logEvent, navigate]);

  const handleSignOut = useCallback(async () => {
    try {
      setIsSigningOut(true);
      await logout();
      localStorage.clear();

      // Clear all state
      setSelectedConnection(null);
      setActiveConnectionId(null);
      setConnections([]);
      setSqlQuery("");
      setNaturalQuery("");
      setQueryResult(null);
      setQueryHistory([]);

      navigate("/landing");
      logEvent("user_signed_out");
    } catch (error) {
      logError(error as Error);
      toast.error("Failed to sign out. Please try again.");
    } finally {
      setIsSigningOut(false);
    }
  }, [logout, logEvent, logError, navigate]);

  // Database connection handlers
  // const connectToDatabase = useCallback(
  //   async (connection: DatabaseConnection) => {
  //     try {
  //       const connectionId = await dbService.connect(connection);
  //       setActiveConnectionId(connectionId);
  //       logEvent("database_connected", { connectionId, type: connection.type });
  //     } catch (error) {
  //       logError(error as Error);
  //       setError(
  //         "Failed to connect to database. Please check your connection settings."
  //       );
  //     }
  //   },
  //   [dbService, logEvent, logError]
  // );

  // Query execution with enhanced error handling
  const executeQuery = useCallback(
    async (sql: string): Promise<QueryResult> => {
      console.log("executeQuery called with sql:", sql);
      // console.log("activeConnectionId from state:", activeConnectionId);

      // Try to get activeConnectionId from state first, then from localStorage as fallback
      const connectionDetail = JSON.parse(
        localStorage.getItem("activeConnection") || "{}"
      );
      let activeConnectionId = connectionDetail.connection_id;

      if (!activeConnectionId) {
        console.log(
          "activeConnectionId not in state, checking localStorage..."
        );
        const storedActiveConnection = localStorage.getItem("activeConnection");
        if (storedActiveConnection) {
          try {
            const parsedConnection = JSON.parse(storedActiveConnection);
            activeConnectionId =
              parsedConnection.id || parsedConnection.connection_id;
            // console.log("Found connectionId in localStorage:", connectionId);

            // Update state for future calls
            if (activeConnectionId) {
              setActiveConnectionId(activeConnectionId);
              setSelectedConnection(parsedConnection);
            }
          } catch (error) {
            console.error("Failed to parse stored connection:", error);
          }
        }
      }

      if (!activeConnectionId) {
        const errorMsg = "No active database connection";
        console.error(errorMsg);
        toast.error(errorMsg + ". Please select a connection first.");
        throw new Error(errorMsg);
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get auth token and set it
        const tokens = localStorage.getItem("token");
        if (tokens) {
          const parsedTokens = JSON.parse(tokens);
          const accessToken = parsedTokens.accessToken;
          if (accessToken) {
            api.setAuthToken(accessToken);
          } else {
            throw new Error("Authentication required. Please log in again.");
          }
        } else {
          throw new Error("No authentication found. Please log in again.");
        }

        const payload: QueryExecutePayload = {
          connection_id: activeConnectionId,
          sql: sql,
        };

        console.log("Executing query with payload:", payload);
        const response = await api.executeQuery(payload);
        console.log("Query execution response:", response);

        // Transform the backend response to match QueryResult interface
        const result: QueryResult = {
          columns: response.columns || [],
          data: response.rows || [],
          rowCount: response.row_count || 0,
          executionTime: response.execution_time_ms || 0,
          error: undefined,
          summary: response.summary || "",
        };

        setQueryResult(result);

        // Persist every executed query so it appears in Query History.
        // Failures here must not surface to the user: the query itself succeeded.
        api
          .saveQuery({
            connection_id: activeConnectionId,
            connection_name: selectedConnection?.name || "Unknown Connection",
            nl_query: naturalQuery || "Manual SQL Query",
            generated_sql: sql,
            execution_time_ms: result.executionTime,
          })
          .then((saved: any) => {
            setQueryHistory((prev) => [
              {
                id: saved?.query_id || saved?.id || Date.now().toString(),
                naturalQuery: naturalQuery || "Manual SQL Query",
                sqlQuery: sql,
                timestamp: new Date(),
                executionTime: result.executionTime,
                favorite: false,
                connection_name: selectedConnection?.name,
              },
              ...prev.slice(0, 99),
            ]);
          })
          .catch((err: any) =>
            console.error("Failed to record query in history:", err)
          );

        // Log successful execution
        logEvent("query_executed", {
          executionTime: result.executionTime,
          rowCount: result.rowCount,
          connectionId: activeConnectionId,
          queryLength: sql.length,
        });

        toast.success(
          `Query executed successfully. ${result.rowCount} rows returned.`
        );
        return result;
      } catch (error: any) {
        const errorMessage = error?.message || "Unknown error occurred";
        logError(error as Error);

        // Handle specific backend error messages
        let userMessage = errorMessage;
        if (error.status === 400 && errorMessage.includes("SQL")) {
          userMessage = "SQL Error: " + errorMessage;
        } else if (error.status === 401) {
          userMessage = "Authentication failed. Please log in again.";
        } else if (error.status === 404) {
          userMessage =
            "Database connection not found. Please check your connection.";
        }

        toast.error(`Query execution failed: ${userMessage}`);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [activeConnectionId, naturalQuery, selectedConnection, logEvent, logError]
  );

  // Auto-save handler
  // const handleAutoSave = useCallback(async () => {
  //   if (!sqlQuery.trim()) return;

  //   try {
  //     // In a real app, this would save to backend
  //     logEvent("query_auto_saved", { queryLength: sqlQuery.length });
  //   } catch (error) {
  //     logError(error as Error);
  //   }
  // }, [sqlQuery, logEvent, logError]);

  // Session timeout handler
  // const handleSessionTimeout = useCallback(() => {
  //   logEvent("session_timeout");
  //   toast.error(
  //     "Your session has expired due to inactivity. Please sign in again."
  //   );
  //   handleSignOut();
  // }, [logEvent, handleSignOut]);

  // Query handlers
  // const handleNLPQuery = useCallback(
  //   async (sql: string, naturalQuery: string) => {
  //     setSqlQuery(sql);
  //     setNaturalQuery(naturalQuery);

  //     try {
  //       await executeQuery(sql);
  //     } catch (error) {
  //       logError(error as Error);
  //     }
  //   },
  //   [executeQuery, logError]
  // );

  const handleQueryChange = useCallback((sql: string) => {
    setSqlQuery(sql);
    setError(null); // Clear any previous errors
  }, []);

  // const handleVisualize = useCallback(async () => {
  //   if (!queryResult) return;

  //   try {
  //     // In a real app, this would generate visualizations
  //     logEvent("visualization_requested", { rowCount: queryResult.length });
  //   } catch (error) {
  //     logError(error as Error);
  //   }
  // }, [queryResult, logEvent, logError]);

  // Team management handlers
  const handleInviteMember = useCallback(
    async (email: string, role: "admin" | "editor" | "viewer") => {
      try {
        // In a real app, this would send invitation via backend
        logEvent("team_member_invited", { email, role });
      } catch (error) {
        logError(error as Error);
        toast.error("Failed to invite team member. Please try again.");
      }
    },
    [logEvent, logError]
  );

  const handleUpdateMemberRole = useCallback(
    (id: string, role: "admin" | "editor" | "viewer") => {
      setTeamMembers((prev) =>
        prev.map((member) => (member.id === id ? { ...member, role } : member))
      );
      logEvent("team_member_role_updated", { memberId: id, role });
    },
    [logEvent]
  );

  // // Query library handlers
  // const handleSelectQuery = useCallback(
  //   async (query: QueryHistoryItem) => {
  //     setSqlQuery(query.sqlQuery);
  //     setNaturalQuery(query.naturalQuery);

  //     try {
  //       await executeQuery(query.sqlQuery);
  //       logEvent("query_selected_from_library", { queryId: query.id });
  //     } catch (error) {
  //       logError(error as Error);
  //     }
  //   },
  //   [executeQuery, logEvent, logError]
  // );

  const handleSelectSavedQuery = useCallback(
    async (query: SavedQuery) => {
      setSqlQuery(query.query);
      setNaturalQuery(query.description);

      try {
        await executeQuery(query.query);
        logEvent("query_selected_from_library", { queryId: query.id });
      } catch (error) {
        logError(error as Error);
      }
    },
    [executeQuery, logEvent, logError]
  );

  const handleToggleQueryFavorite = useCallback(
    (id: string) => {
      setSavedQueries((prev) =>
        prev.map((query) =>
          query.id === id ? { ...query, favorite: !query.favorite } : query
        )
      );
      logEvent("query_favorite_toggled", { queryId: id });
    },
    [logEvent]
  );

  const handleSaveQuery = useCallback(async () => {
    //#region ---- commented -----
    // if (!sqlQuery.trim()) {
    //   toast.error("Cannot save empty query");
    //   return;
    // }
    // try {
    //   const newQuery = {
    //     id: Date.now().toString(),
    //     name: `Query ${Date.now()}`,
    //     description: naturalQuery || "Saved query",
    //     query: sqlQuery,
    //     tags: [],
    //     favorite: false,
    //     lastRun: new Date(),
    //     folder: "General",
    //   };
    //   setSavedQueries((prev) => [newQuery, ...prev]);
    //   logEvent("query_saved", { queryId: newQuery.id });
    // } catch (error) {
    //   logError(error as Error);
    //   toast.error("Failed to save query. Please try again.");
    // }
    //#endregion

    // if (!sqlQuery.trim()) {
    //   toast.error("Cannot save empty query");
    //   return;
    // }

    // if (!activeConnectionId) {
    //   toast.error("Please select a database connection first");
    //   return;
    // }

    try {
      // Get connection name from selectedConnection
      const connectionName = selectedConnection?.name || "Unknown Connection";

      // Get auth token
      const tokens = localStorage.getItem("token");
      if (tokens) {
        const parsedTokens = JSON.parse(tokens);
        const accessToken = parsedTokens.accessToken;
        if (accessToken) {
          api.setAuthToken(accessToken);
        }
      }

      const activeConnection = JSON.parse(
        localStorage.getItem("activeConnection") || "{}"
      );
      let activeConnectionId = activeConnection.id;

      const payload: SaveQueryPayload = {
        connection_id: activeConnectionId,
        connection_name: connectionName,
        nl_query: naturalQuery || "Manual SQL Query",
        generated_sql: sqlQuery,
        execution_time_ms: queryResult?.executionTime || 0,
      };

      console.log("Saving query with payload:", payload);
      const savedQuery = await api.saveQuery(payload);
      console.log("Query saved successfully:", savedQuery);

      // Add to local state
      const newQuery = {
        id: savedQuery.id || Date.now().toString(),
        name: (naturalQuery || "Saved Query").substring(0, 50),
        description: naturalQuery || "Saved query",
        query: sqlQuery,
        tags: [],
        favorite: false,
        lastRun: new Date(),
        folder: "General",
        executionTime: queryResult?.executionTime || 0,
      };
      setSavedQueries((prev) => [newQuery, ...prev]);

      // Also add to query history
      const historyItem = {
        id: savedQuery.id || Date.now().toString(),
        naturalQuery: naturalQuery || "Manual SQL Query",
        sqlQuery: sqlQuery,
        timestamp: new Date(),
        executionTime: queryResult?.executionTime || 0,
        favorite: false,
        connection_name: connectionName,
      };
      // setQueryHistory((prev) => [historyItem, ...prev.slice(0, 99)]);

      // logEvent("query_saved", { queryId: savedQuery.id });
      // toast.success("Query saved successfully!");
    } catch (error: any) {
      console.error("Failed to save query:", error);
      logError(error as Error);
      toast.error(error.message || "Failed to save query. Please try again.");
    }
  }, [
    sqlQuery,
    naturalQuery,
    activeConnectionId,
    queryResult,
    selectedConnection,
    logEvent,
    logError,
  ]);

  const handleRunQuery = useCallback(
    async (sqlToExecute?: string) => {
      const queryToRun = sqlToExecute !== undefined ? sqlToExecute : sqlQuery;

      console.log("handleRunQuery called");
      console.log("sqlToExecute parameter:", sqlToExecute);
      console.log("sqlQuery from state:", sqlQuery);
      console.log("queryToRun:", queryToRun);
      // console.log("activeConnectionId:", activeConnectionId);
      console.log("selectedConnection:", selectedConnection);

      if (!queryToRun.trim()) {
        toast.error("Please enter a query to execute");
        return;
      }

      const activeConnection = JSON.parse(
        localStorage.getItem("activeConnection") || "{}"
      );
      let activeConnectionId = activeConnection.id;

      if (!activeConnectionId) {
        toast.error("Please select a database connection first");
        return;
      }

      try {
        await executeQuery(queryToRun);
      } catch (error) {
        logError(error as Error);
        console.error("Query execution error:", error);
      }
    },
    [sqlQuery, executeQuery, logError, activeConnectionId, selectedConnection]
  );

  // Connection management handlers
  const handleAddConnection = useCallback(
    (connection: DatabaseConnection) => {
      const updatedConnections = [connection, ...connections];
      setConnections(updatedConnections);
      setSelectedConnection(connection);
      setActiveConnectionId(connection.id); // Set the active connection ID

      // Store in localStorage
      localStorage.setItem("connections", JSON.stringify(updatedConnections));
      localStorage.setItem("activeConnection", JSON.stringify(connection));

      logEvent("connection_added", {
        connectionId: connection.id,
        type: connection.type,
      });
    },
    [connections, logEvent]
  );

  // const handleSelectConnection = useCallback(
  //   (connection: DatabaseConnection) => {
  //     setSelectedConnection(connection);
  //     logEvent("connection_selected", { connectionId: connection.id });
  //   },
  //   [logEvent]
  // );

  // const handleRemoveConnection = useCallback(
  //   (connectionId: string) => {
  //     setConnections((prev) => prev.filter((conn) => conn.id !== connectionId));
  //     if (selectedConnection?.id === connectionId) {
  //       setSelectedConnection(null);
  //     }
  //     logEvent("connection_removed", { connectionId });
  //   },
  //   [selectedConnection, logEvent]
  // );

  // Notification handlers
  const handleDismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const handleMarkNotificationAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  const handleClearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleMarkAllNotificationsAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  // Redirect to landing page if not authenticated and not on auth routes
  React.useEffect(() => {
    const authRoutes = ["/", "/login", "/signup", "/forgot-password"];
    const isAuthRoute = authRoutes.includes(location.pathname);

    if (!user && !isAuthRoute) {
      navigate("/");
    }
  }, [user, location.pathname, navigate]);

  const handleSelectQueryHistoryItem = useCallback(
    (item: QueryHistoryItem) => {
      setSqlQuery(item.generated_sql);
      setNaturalQuery(item.nl_query);
      navigate("/query");
    },
    [navigate]
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        {/* Network Status Banner */}
        {/* {!isOnline && (
          <div className="bg-red-500 text-white text-center py-2 px-4">
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              You are currently offline. Some features may be unavailable.
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.reload()}
                icon={<RefreshCw className="w-4 h-4" />}
              >
                Retry
              </Button>
            </div>
          </div>
        )} */}

        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-400 text-center py-2 px-4">
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Navigation - Only show for authenticated users */}
        {user && (
          <nav className="sticky top-0 z-50 bg-gray-900 border-b border-gray-800">
            <div className="mx-auto pl-3 pr-3">
              <div className="flex items-center justify-between h-16">
                {/* Left side - Logo and Brand */}
                <div
                  className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleNavigateToLanding}
                >
                  <Terminal className="w-8 h-8 text-blue-400" />
                  <h1 className="text-2xl font-bold">QueryVerse</h1>
                  <span className="text-xs text-gray-400">v1.0.0</span>
                </div>

                {/* Center - Main Navigation */}
                <div className="flex space-x-1 bg-gray-800 rounded-lg p-1">
                  <Button
                    variant={
                      location.pathname === "/query" ? "primary" : "ghost"
                    }
                    size="sm"
                    onClick={() => navigate("/query")}
                    icon={<Database className="w-4 h-4" />}
                  >
                    Query Editor
                  </Button>
                  <Button
                    variant={
                      location.pathname.startsWith("/notebook")
                        ? "primary"
                        : "ghost"
                    }
                    size="sm"
                    onClick={() => navigate("/notebooks")}
                    icon={<BookOpen className="w-4 h-4" />}
                  >
                    Research Notebook
                  </Button>
                  <Button
                    variant={
                      location.pathname === "/builder" ? "primary" : "ghost"
                    }
                    size="sm"
                    onClick={() => navigate("/builder")}
                    icon={<Compass className="w-4 h-4" />}
                  >
                    Visual Builder
                  </Button>
                  <Button
                    variant={
                      location.pathname === "/dashboard" ? "primary" : "ghost"
                    }
                    size="sm"
                    onClick={() => navigate("/dashboard")}
                    icon={<Layout className="w-4 h-4" />}
                  >
                    Dashboard
                  </Button>
                </div>

                {/* Right side - User Actions */}
                <div className="flex items-center gap-4">
                  <Suspense fallback={<div className="w-5 h-5" />}>
                    <NotificationCenter
                      notifications={notifications}
                      onDismiss={handleDismissNotification}
                      onMarkAsRead={handleMarkNotificationAsRead}
                      onClearAll={handleClearAllNotifications}
                      onMarkAllAsRead={handleMarkAllNotificationsAsRead}
                    />
                  </Suspense>

                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/admin")}
                      icon={<Cog className="w-5 h-5" />}
                    >
                      Admin
                    </Button>
                  )}

                  <UserMenu
                    user={user}
                    isSigningOut={isSigningOut}
                    onSignOut={handleSignOut}
                  />
                  {/* <div className="flex items-center gap-2 text-gray-300">
                    <Shield className="w-5 h-5" />
                    <span>{user?.email}</span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    loading={isSigningOut}
                    loadingText="Signing out..."
                    onClick={handleSignOut}
                    icon={<LogOut className="w-4 h-4" />}
                  >
                    Sign Out
                  </Button> */}
                </div>
              </div>
            </div>
          </nav>
        )}

        {/* Left Sidebar - Only show for authenticated users */}
        {user && (
          <div className="fixed left-0 top-16 bottom-0 w-64 bg-gray-900 border-r border-gray-800 p-4 z-40">
            <div className="space-y-2">
              {/* Data Sources */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Data Sources
                </h3>
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/connections")}
                    icon={<Database className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Connections
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/data-catalog")}
                    icon={<Server className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Data Catalog
                  </Button>
                </div>
              </div>

              {/* Tools */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Tools
                </h3>
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/query-library")}
                    icon={<BookMarked className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Query Library
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/mindmap")}
                    icon={<Brain className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Mind Maps
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/scheduled-tasks")}
                    icon={<Clock className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Scheduled Tasks
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/query-history")}
                    icon={<History className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Query History
                  </Button>
                </div>
              </div>

              {/* Collaboration */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Collaboration
                </h3>
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/team")}
                    icon={<Users className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Team Members
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/shared-queries")}
                    icon={<Share2 className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Shared Queries
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/comments")}
                    icon={<MessageSquare className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Comments
                  </Button>
                </div>
              </div>

              {/* Settings */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Settings
                </h3>
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/profile")}
                    icon={<UserCircle className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Profile
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/security")}
                    icon={<Key className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Security
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/settings")}
                    icon={<Settings className="w-4 h-4" />}
                    className="w-full justify-start"
                  >
                    Preferences
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        {!user ? (
          /* Render public routes without layout when not authenticated */
          <Suspense fallback={<LoadingScreen type="data" />}>
            <AppRoutes
              user={user}
              sqlQuery={sqlQuery}
              isLoading={isLoading}
              queryResult={queryResult}
              selectedConnection={selectedConnection}
              connections={connections}
              performanceMetrics={performanceMetrics}
              queryHistory={queryHistory}
              savedQueries={savedQueries}
              teamMembers={teamMembers}
              handleQueryChange={handleQueryChange}
              handleSaveQuery={handleSaveQuery}
              handleRunQuery={handleRunQuery}
              executeQuery={executeQuery}
              setSqlQuery={setSqlQuery}
              setNaturalQuery={setNaturalQuery}
              setSelectedConnection={setSelectedConnection}
              setConnections={setConnections}
              setActiveConnectionId={setActiveConnectionId}
              resultsRef={resultsRef}
              handleAddConnection={handleAddConnection}
              handleInviteMember={handleInviteMember}
              handleUpdateMemberRole={handleUpdateMemberRole}
              handleSelectSavedQuery={handleSelectSavedQuery}
              handleToggleQueryFavorite={handleToggleQueryFavorite}
              isAdmin={isAdmin}
              handleSelectQueryHistoryItem={handleSelectQueryHistoryItem}
            />
          </Suspense>
        ) : (
          /* Render authenticated routes with layout */
          <div className="pl-64">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <Suspense fallback={<LoadingScreen type="data" />}>
                <AppRoutes
                  user={user}
                  sqlQuery={sqlQuery}
                  isLoading={isLoading}
                  queryResult={queryResult}
                  selectedConnection={selectedConnection}
                  connections={connections}
                  performanceMetrics={performanceMetrics}
                  queryHistory={queryHistory}
                  savedQueries={savedQueries}
                  teamMembers={teamMembers}
                  handleQueryChange={handleQueryChange}
                  handleSaveQuery={handleSaveQuery}
                  handleRunQuery={handleRunQuery}
                  executeQuery={executeQuery}
                  setSqlQuery={setSqlQuery}
                  setNaturalQuery={setNaturalQuery}
                  setSelectedConnection={setSelectedConnection}
                  setConnections={setConnections}
                  setActiveConnectionId={setActiveConnectionId}
                  resultsRef={resultsRef}
                  handleAddConnection={handleAddConnection}
                  handleInviteMember={handleInviteMember}
                  handleUpdateMemberRole={handleUpdateMemberRole}
                  handleSelectSavedQuery={handleSelectSavedQuery}
                  handleToggleQueryFavorite={handleToggleQueryFavorite}
                  isAdmin={isAdmin}
                  handleSelectQueryHistoryItem={handleSelectQueryHistoryItem}
                />
              </Suspense>
            </div>
          </div>
        )}

        {/* The routing is now handled in AppRoutes component */}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </ErrorBoundary>
  );
};

export default App;
